import React, { useState, useEffect, ReactElement, FC, useCallback, useMemo, ChangeEvent } from 'react';
import i18next from 'i18next';
import { Form, Spinner } from 'react-bootstrap';
import { ITherapy } from '../../../util/types';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import {
  PromQuestionConfigurationDto,
  PromQuestionDto, PromQuestionLocalizedContentDto,
  TherapyPromMappingDto
} from '../../../../../shared/dtos/prom.dto';
import { PromTriggerType } from '../../../../../shared/utils/enums';
import { emitErrorNotification, emitSuccessNotification } from '../../../components/Notification';
import { PromQuestionConfigurationRequest } from '../../../../../shared/requests/prom.request';
import PrimaryButton from '../../../components/PrimaryButton';
import LoadingSpinner from '../../../components/LoadingSpinner';
import EmptyState from '../../../components/EmptyState';

type PromQuestionDetailsProps = {
  therapy: ITherapy;
};

type SelectablePromQuestion = PromQuestionDto & {
  isSelected: boolean;
}

const TRIGGER_ORDER: Record<PromTriggerType, number> = {
  [PromTriggerType.AFTER_EVERY_TRAINING]: 0,
  [PromTriggerType.WEEKLY]: 1,
  [PromTriggerType.AFTER_ENDURANCE_TRAINING]: 2,
};

const getLocalizedContent = (promQuestion: PromQuestionDto, languageCode: string): PromQuestionLocalizedContentDto | null => {
  return promQuestion.localizeContent.find((content: PromQuestionLocalizedContentDto) => content.languageCode === languageCode) 
    ?? promQuestion.localizeContent[0] 
    ?? null;
}

const PromQuestionDetails: FC<PromQuestionDetailsProps> = ({ therapy }: PromQuestionDetailsProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  
  const [promQuestions, setPromQuestions] = useState<SelectablePromQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  
  const selectedPromQuestionIds: string[] = useMemo(() => {
    return promQuestions.filter(({ isSelected }: SelectablePromQuestion) => isSelected).map(({ id }: SelectablePromQuestion) => id);
  }, [promQuestions]);
  
  const fetchPromConfiguration: (showLoading: boolean) => Promise<void> = useCallback(async (showLoading: boolean = true): Promise<void> => {
    if (showLoading) {
      setIsLoading(true);
    }
    
    try {
      const response: PromQuestionConfigurationDto = await authFetch<PromQuestionConfigurationDto>(`/api/v1/promquestions/therapy/${therapy.id}/configuration`);
      const selectedIds: Set<string> = new Set(response.selectedPromQuestionIds);

      const questions: SelectablePromQuestion[] = response.promQuestions.map((question: PromQuestionDto) => ({
        ...question,
        isSelected: selectedIds.has(question.id),
      }));

      questions.sort(
        (a: SelectablePromQuestion, b: SelectablePromQuestion) =>
          (TRIGGER_ORDER[a.trigger] ?? Number.MAX_SAFE_INTEGER) -
          (TRIGGER_ORDER[b.trigger] ?? Number.MAX_SAFE_INTEGER),
      );

      setPromQuestions(questions);
    } catch (_) {
      emitErrorNotification(i18next.t('generic.unknownError'))
    } finally {
      setIsLoading(false);
    }
  }, [authFetch, therapy.id])
  

  useEffect(() => {
    void fetchPromConfiguration(true);
  }, [fetchPromConfiguration]);
  
  const savePromConfiguration = async (): Promise<void> => {
    setIsSaving(true);
    
    const request: PromQuestionConfigurationRequest = {
      promQuestionIds: selectedPromQuestionIds,
    }
    
    try {
      await authFetch<TherapyPromMappingDto>(`/api/v1/promquestions/therapy/${therapy.id}/configuration`, {
        method: 'PUT',
        body: request,
      });
      
      await fetchPromConfiguration(false);
      setIsSaved(true);
      emitSuccessNotification(i18next.t('promQuestionnaireDetails.saveSuccess'))
    } catch (_) {
      emitErrorNotification(i18next.t('generic.unknownError'))
    } finally {
      setIsSaving(false);
    }
  }
  
  const handleChecked = (event: ChangeEvent<HTMLInputElement>, selectedPromQuestionId: string): void => {
    const isSelected: boolean = event.target.checked;
    
    setPromQuestions((current: SelectablePromQuestion[]) => current.map((question: SelectablePromQuestion) => {
      if (question.id !== selectedPromQuestionId) {
        return question;
      }
      
      return {
        ...question,
        isSelected
      };
    }))
    
    setIsSaved(false);
  }
  
  const renderPromQuestion = (promQuestion: SelectablePromQuestion): ReactElement => {
    const localizedContent: PromQuestionLocalizedContentDto | null  = getLocalizedContent(promQuestion, i18next.language);
    
    return (
      <div
        key={promQuestion.id}
        className="d-flex align-items-start gap-3 border rounded-3 p-3"
        style={
          promQuestion.isSelected
            ? {
              borderColor: 'rgba(255, 105, 95, 0.55)',
              backgroundColor: 'rgba(255, 105, 95, 0.05)',
              boxShadow: 'inset 3px 0 0 #ff695f',
            }
            : undefined
        }
      >
        <Form.Check
          aria-label={localizedContent?.title ?? i18next.t('promQuestionnaireDetails.question')}
          checked={promQuestion.isSelected}
          className="mt-1"
          type="checkbox"
          onChange={(event: ChangeEvent<HTMLInputElement>) => handleChecked(event, promQuestion.id)}
        />

        <div className="flex-grow-1">
          <div className="fw-semibold">{localizedContent?.title}</div>

          {localizedContent?.questionText && (
            <div className="text-body-secondary mt-1">{localizedContent.questionText}</div>
          )}

          {localizedContent?.rangeLabels && (
            <div className="small text-muted mt-2">
              {localizedContent.rangeLabels.min} / {localizedContent.rangeLabels.max}
            </div>
          )}
        </div>

        <span className="badge badge-bg-info flex-shrink-0">
          {i18next.t(`promQuestionnaireDetails.trigger.${promQuestion.trigger}`)}
        </span>
      </div>
    )
  }


  return (
    <section aria-label={i18next.t('promQuestionnaireDetails.title')} className="d-flex flex-column flex-grow-1 p-2">
      <p className="mb-3">
        {i18next.t('promQuestionnaireDetails.introText')}
      </p>

      {isLoading && <LoadingSpinner />}

      {!isLoading && promQuestions.length === 0 && (
        <EmptyState icon="fas fa-clipboard-list" message={i18next.t('promQuestionnaireDetails.noQuestionsAvailable')} />
      )}

      {!isLoading && promQuestions.length > 0 && (
        <div className="d-flex flex-column gap-2 mb-3">{promQuestions.map(renderPromQuestion)}</div>
      )}

      <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
        <PrimaryButton disabled={isLoading || isSaving} onClick={savePromConfiguration}>
          {isSaving && (
            <Spinner
              animation="border"
              aria-hidden="true"
              as="span"
              className="me-2"
              role="status"
              size="sm"
            />
          )}

          {isSaving ? i18next.t('generic.saving') : i18next.t('promQuestionnaireDetails.saveButton')}
        </PrimaryButton>

        {isSaved && (
          <span>{i18next.t('promQuestionnaireDetails.saveText')}</span>
        )}
      </div>
    </section>
  )
}

export default PromQuestionDetails;
